import { motion } from 'framer-motion';
import { useRef } from 'react';

const AboutUsSection = () => {
  const containerRef = useRef(null);

  return (
    <section ref={containerRef} className="py-24 bg-gradient-to-b from-blue-50 to-white relative overflow-hidden">
      {/* Background Decorative Elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <svg className="absolute top-0 right-0 text-blue-100 w-1/3 opacity-50" viewBox="0 0 200 200">
          <path fill="currentColor" d="M44.7,-76.4C58.8,-69.2,71.8,-59.1,79.6,-45.8C87.4,-32.6,90,-16.3,89.4,-0.3C88.8,15.7,85,31.3,76.8,44.3C68.7,57.3,56.2,67.6,42.3,74.3C28.4,81,14.2,84,-0.7,85.1C-15.6,86.3,-31.1,85.6,-45.4,79.7C-59.7,73.8,-72.7,62.7,-79.9,48.1C-87.1,33.5,-88.4,16.8,-86.7,0.9C-85,-14.9,-80.3,-29.8,-71.5,-41.7C-62.7,-53.6,-49.9,-62.4,-36.6,-70C-23.3,-77.6,-11.7,-83.9,2.2,-87.8C16,-91.7,32,-83.2,44.7,-76.4Z" transform="translate(100 100)" />
        </svg>
        
        <svg className="absolute bottom-0 left-0 text-blue-100 w-1/4 opacity-50" viewBox="0 0 200 200">
          <path fill="currentColor" d="M39.9,-65.7C54.1,-60.5,69.3,-53.3,77.9,-41.3C86.4,-29.3,88.3,-12.7,85.4,2.9C82.6,18.5,75,33,65.4,45.2C55.8,57.4,44.1,67.3,30.7,73.7C17.3,80.1,2.2,83,-13.4,81.6C-29,80.2,-45.1,74.4,-57.4,64.1C-69.7,53.8,-78.2,39,-83.2,23C-88.2,7,-89.7,-10.2,-84.9,-25.2C-80.1,-40.2,-69,-52.9,-55.3,-58.5C-41.5,-64.1,-25.1,-62.6,-10.1,-61.7C4.9,-60.8,25.7,-70.9,39.9,-65.7Z" transform="translate(100 100)" />
        </svg>
      </div>

      <div className="max-w-6xl mx-auto px-4">
        {/* Header Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="text-center mb-20"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-blue-900 mb-6">
            Innovating Tomorrow
          </h2>
          <div className="w-24 h-1 bg-blue-500 mx-auto mb-8" />
          <p className="text-blue-700 text-lg max-w-2xl mx-auto">
            We're a dynamic startup with a team of seasoned experts, poised to transform innovative ideas into groundbreaking solutions.
          </p>
        </motion.div>

        {/* Core Values Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-20">
          {[
            {
              icon: (
                <svg className="w-12 h-12 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              ),
              title: "Innovation First",
              description: "Pushing boundaries with cutting-edge solutions and fresh perspectives"
            },
            {
              icon: (
                <svg className="w-12 h-12 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              ),
              title: "Expert Team",
              description: "Combining years of industry expertise with fresh perspectives"
            },
            {
              icon: (
                <svg className="w-12 h-12 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                </svg>
              ),
              title: "Future Ready",
              description: "Building scalable solutions for tomorrow's challenges"
            }
          ].map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.2 }}
              className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow 
                         border border-blue-100 group hover:border-blue-200"
            >
              <div className="mb-6 transform group-hover:scale-110 transition-transform">
                {item.icon}
              </div>
              <h3 className="text-xl font-semibold text-blue-900 mb-4">
                {item.title}
              </h3>
              <p className="text-blue-600">
                {item.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Stats Section */}
        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          className="bg-blue-900 rounded-3xl p-12 text-white relative overflow-hidden"
        >
          <div className="grid md:grid-cols-3 gap-8 relative z-10">
            {[
              { number: "5+", label: "Expert Team Members" },
              { number: "8+", label: "Technologies Mastered" },
              { number: "10+", label: "Years Combined Experience" }
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                className="text-center"
              >
                <div className="text-4xl font-bold mb-2">{stat.number}</div>
                <div className="text-blue-200">{stat.label}</div>
              </motion.div>
            ))}
          </div>
          
          {/* Background Pattern */}
          <svg className="absolute top-0 right-0 text-blue-800 w-1/2 h-full opacity-20" viewBox="0 0 200 200">
            <path fill="currentColor" d="M39.9,-65.7C54.1,-60.5,69.3,-53.3,77.9,-41.3C86.4,-29.3,88.3,-12.7,85.4,2.9C82.6,18.5,75,33,65.4,45.2C55.8,57.4,44.1,67.3,30.7,73.7C17.3,80.1,2.2,83,-13.4,81.6C-29,80.2,-45.1,74.4,-57.4,64.1C-69.7,53.8,-78.2,39,-83.2,23C-88.2,7,-89.7,-10.2,-84.9,-25.2C-80.1,-40.2,-69,-52.9,-55.3,-58.5C-41.5,-64.1,-25.1,-62.6,-10.1,-61.7C4.9,-60.8,25.7,-70.9,39.9,-65.7Z" transform="translate(100 100)" />
          </svg>
        </motion.div>
      </div>
    </section>
  );
};

export default AboutUsSection;
